import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

/**
 * PDF "Orden de Produccion" de una orden de maquila, con el formato que usan en
 * Excel: encabezado (folio, fechas, OC, cliente, marca, tela, lavado), piezas
 * por color y talla, habilitacion para maquila y para empaque, imagenes del
 * articulo y de la habilitacion, y firmas.
 *
 * `ficha` es lo que devuelve GET /cortes/:id/ficha (las imagenes ya vienen en
 * base64). Devuelve el jsPDF; quien llama decide si lo abre o lo guarda.
 */
const VERDE = [52, 64, 30]; // color del logo de Cavanna
const GRIS = [235, 237, 230];
const M = 8; // margen

const fmt = (n, dec = 0) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: dec, minimumFractionDigits: 0 }).format(Number(n) || 0);
const fecha = (f) => {
  if (!f) return "";
  const s = String(f).slice(0, 10);
  const [a, m, d] = s.split("-");
  return a && m && d ? `${d}/${m}/${a}` : s;
};

async function cargarLogo() {
  try {
    const r = await fetch(`${process.env.PUBLIC_URL || ""}/logoInstitucion.png`);
    if (!r.ok) return null;
    const blob = await r.blob();
    return await new Promise((ok) => {
      const lector = new FileReader();
      lector.onload = () => ok(lector.result);
      lector.onerror = () => ok(null);
      lector.readAsDataURL(blob);
    });
  } catch (e) {
    return null;
  }
}

/** Mete la imagen en la caja sin deformarla y centrada. */
function imagenEnCaja(doc, img, x, y, ancho, alto) {
  if (!img || !img.dataUrl) return;
  const proporcion = img.ancho && img.alto ? img.ancho / img.alto : 1;
  let w = ancho;
  let h = w / proporcion;
  if (h > alto) {
    h = alto;
    w = h * proporcion;
  }
  doc.addImage(img.dataUrl, "JPEG", x + (ancho - w) / 2, y + (alto - h) / 2, w, h);
}

export async function pdfOrdenProduccion(ficha, { elaboradoPor = "" } = {}) {
  const doc = new jsPDF("portrait", "mm", "letter");
  const ANCHO = doc.internal.pageSize.getWidth();
  const ALTO = doc.internal.pageSize.getHeight();
  const { orden, pedido, articulo, piezas, habilitacion, imagenes } = ficha;
  const art = articulo || {};
  const ped = pedido || {};

  // ---- Encabezado
  const logo = await cargarLogo();
  if (logo) doc.addImage(logo, "PNG", M, 4, 42, 28);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(...VERDE);
  doc.text("ORDEN DE PRODUCCIÓN", ANCHO / 2 - 2, 18, { align: "center" });
  doc.setTextColor(0);
  doc.setFontSize(10);
  doc.setDrawColor(...VERDE);
  doc.roundedRect(ANCHO - M - 52, 8, 52, 18, 2, 2);
  doc.text("FOLIO:", ANCHO - M - 49, 14);
  doc.setFontSize(13);
  // Ordenes anteriores al folio CAV: sale el codigo del articulo, como hasta ahora.
  const folio = orden.folioCAV || art.codigo || "";
  doc.text(String(folio), ANCHO - M - 3, 14, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.text(orden.folioCAV ? `Modelo ${art.codigo || ""}` : `Orden de maquila ${orden.numero || ""}`, ANCHO - M - 3, 22, { align: "right" });

  // ---- Datos generales (dos columnas)
  const dato = (etiqueta, valor) => [{ content: etiqueta, styles: { fontStyle: "bold", textColor: VERDE } }, valor || ""];
  const izquierda = [
    dato("FECHA", fecha(orden.fecha)),
    dato("FECHA PEDIDO", fecha(ped.fecha)),
    dato("FECHA DE ENTREGA", fecha(orden.fechaCompromiso && orden.fechaCompromiso !== "Sin fecha" ? orden.fechaCompromiso : ped.fechaCancelacion)),
    dato("OC.", ped.pedidoCliente || "PENDIENTE"),
    dato("CLIENTE", ped.cliente),
    dato("DEPARTAMENTO", art.departamento),
  ];
  const derecha = [
    dato("MARCA", art.marca),
    dato("TELA", art.tela),
    dato("LAVADO", art.lavado),
    dato("MAQUILERO", orden.maquilero),
    dato("PEDIDO", ped.numero ? String(ped.numero) : ""),
    dato("PRECIO MAQUILA", orden.precioMaquila ? `$${fmt(orden.precioMaquila, 2)} x prenda` : ""),
  ];
  const estiloDatos = {
    theme: "plain",
    styles: { fontSize: 8.5, cellPadding: 1.1 },
    columnStyles: { 0: { cellWidth: 32 } },
  };
  const anchoMitad = (ANCHO - 2 * M - 6) / 2;
  autoTable(doc, { ...estiloDatos, body: izquierda, startY: 34, margin: { left: M, right: ANCHO - M - anchoMitad } });
  const finIzq = doc.lastAutoTable.finalY;
  autoTable(doc, { ...estiloDatos, body: derecha, startY: 34, margin: { left: M + anchoMitad + 6, right: M } });
  let y = Math.max(finIzq, doc.lastAutoTable.finalY) + 3;

  // ---- Modelo: piezas por color y talla
  const cuerpoPiezas = piezas.map((p, i) => [
    i === 0 ? { content: art.modelo || "", rowSpan: piezas.length } : null,
    i === 0 ? { content: art.descripcion || "", rowSpan: piezas.length } : null,
    p.color,
    p.talla,
    p.sku,
    fmt(p.cantidadPedido),
    fmt(p.cantidad),
  ].filter((c) => c !== null));
  autoTable(doc, {
    startY: y,
    head: [["MODELO", "DESCRIPCIÓN", "COLOR", "TALLA", "SKU", "PEDIDO", "A PRODUCIR"]],
    body: cuerpoPiezas,
    foot: [["", "", "", "", "TOTAL", fmt(piezas.reduce((t, p) => t + p.cantidadPedido, 0)), fmt(orden.totalProducir)]],
    theme: "grid",
    styles: { fontSize: 7.5, cellPadding: 1.2, valign: "middle" },
    headStyles: { fillColor: VERDE, textColor: 255, halign: "center" },
    footStyles: { fillColor: GRIS, textColor: 0, fontStyle: "bold" },
    columnStyles: {
      0: { cellWidth: 22, fontStyle: "bold" },
      1: { cellWidth: 62 },
      5: { halign: "right" },
      6: { halign: "right" },
    },
    margin: { left: M, right: M },
  });
  y = doc.lastAutoTable.finalY + 4;

  // ---- Habilitacion (izquierda) e imagenes (derecha)
  const anchoImagenes = 66;
  const xImagenes = ANCHO - M - anchoImagenes;
  const margenTablas = { left: M, right: anchoImagenes + M + 3 };
  const inicioSeccion = y;
  const paginaSeccion = doc.getCurrentPageInfo().pageNumber;
  const tabla = (titulo, lista) => {
    autoTable(doc, {
      startY: y,
      head: [
        [{ content: titulo, colSpan: 6, styles: { halign: "center", fillColor: VERDE, textColor: 255 } }],
        ["DESCRIPCIÓN", "PIEZAS", "MARCA", "COLOR", "PROVEEDOR", "TOTAL"],
      ],
      body: lista.length
        ? lista.map((h) =>
            // La composicion de tela es solo texto: ocupa el resto del renglon.
            h.texto !== undefined
              ? [h.tipo, { content: h.texto, colSpan: 5 }]
              : [h.tipo, fmt(h.piezas, 2), h.marca, h.color, h.proveedor, fmt(h.total, 2)]
          )
        : [[{ content: "Sin habilitación registrada", colSpan: 6, styles: { halign: "center", textColor: 120 } }]],
      theme: "grid",
      styles: { fontSize: 6.8, cellPadding: 1, overflow: "linebreak" },
      headStyles: { fillColor: GRIS, textColor: VERDE, fontStyle: "bold", halign: "center" },
      columnStyles: { 0: { cellWidth: 33 }, 1: { halign: "center", cellWidth: 11 }, 5: { halign: "right", cellWidth: 14 } },
      margin: margenTablas,
    });
    y = doc.lastAutoTable.finalY + 3;
  };
  tabla("HABILITACIÓN PARA MAQUILA", habilitacion.filter((h) => h.grupo === "Maquila"));
  tabla("HABILITACIÓN PARA EMPAQUE", habilitacion.filter((h) => h.grupo === "Empaque"));
  const finTablas = y;
  const paginaTablas = doc.getCurrentPageInfo().pageNumber;

  // Imagenes: columna derecha, desde la pagina donde empezo la seccion.
  doc.setPage(paginaSeccion);
  let paginaImagenes = paginaSeccion;
  let yi = inicioSeccion;
  doc.setFillColor(...VERDE);
  doc.rect(xImagenes, yi, anchoImagenes, 6, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(255);
  doc.text("IMÁGENES", xImagenes + anchoImagenes / 2, yi + 4.2, { align: "center" });
  doc.setTextColor(0);
  yi += 8;
  // Cuadricula de fotos en la columna derecha; si ya no cabe, sigue en la pagina siguiente.
  const cuadricula = (fotosCuadricula, porRenglon, conEtiqueta) => {
    const lado = (anchoImagenes - 3 * (porRenglon - 1)) / porRenglon;
    const altoCelda = lado + (conEtiqueta ? 4 : 0) + 3;
    fotosCuadricula.forEach(({ img, etiqueta }, i) => {
      const col = i % porRenglon;
      if (col === 0 && i > 0) yi += altoCelda;
      if (col === 0 && yi + altoCelda > ALTO - M) {
        paginaImagenes += 1;
        if (paginaImagenes > doc.getNumberOfPages()) doc.addPage();
        doc.setPage(paginaImagenes);
        yi = M;
      }
      const x = xImagenes + col * (lado + 3);
      doc.setDrawColor(210);
      doc.rect(x, yi, lado, lado);
      imagenEnCaja(doc, img, x + 0.5, yi + 0.5, lado - 1, lado - 1);
      if (conEtiqueta) {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(6);
        doc.text(doc.splitTextToSize(etiqueta, lado)[0], x + lado / 2, yi + lado + 3, { align: "center" });
      }
    });
    if (fotosCuadricula.length) yi += altoCelda;
  };

  // Fotos del articulo completas (sin recortar). Una sola va a todo el ancho;
  // con varias van de dos en dos y del mismo tamano (delantera y trasera).
  const fotosArticulo = (imagenes && (imagenes.articulos || (imagenes.articulo ? [imagenes.articulo] : []))) || [];
  if (fotosArticulo.length === 1) {
    doc.setDrawColor(200);
    doc.rect(xImagenes, yi, anchoImagenes, 74);
    imagenEnCaja(doc, fotosArticulo[0], xImagenes + 1, yi + 1, anchoImagenes - 2, 72);
    yi += 77;
  } else if (fotosArticulo.length > 1) {
    const ancho = (anchoImagenes - 2) / 2;
    for (let i = 0; i < fotosArticulo.length; i += 2) {
      const par = fotosArticulo.slice(i, i + 2);
      // Alto de la caja segun la foto mas alta del par, para que se vean completas.
      const proporcion = Math.max(...par.map((f) => (f.ancho && f.alto ? f.alto / f.ancho : 1)));
      const alto = Math.min(90, Math.max(40, (ancho - 1) * proporcion + 1));
      if (yi + alto > ALTO - M) {
        paginaImagenes += 1;
        if (paginaImagenes > doc.getNumberOfPages()) doc.addPage();
        doc.setPage(paginaImagenes);
        yi = M;
      }
      par.forEach((f, j) => {
        const x = xImagenes + j * (ancho + 2);
        doc.setDrawColor(200);
        doc.rect(x, yi, ancho, alto);
        imagenEnCaja(doc, f, x + 0.5, yi + 0.5, ancho - 1, alto - 1);
      });
      yi += alto + 3;
    }
  } else {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(140);
    doc.text("El artículo no tiene foto", xImagenes + anchoImagenes / 2, yi + 6, { align: "center" });
    doc.setTextColor(0);
    yi += 10;
  }
  // Miniaturas de la habilitacion, tres por renglon.
  const conFoto = habilitacion.filter((h) => imagenes && imagenes.habilitacion && imagenes.habilitacion[h._id]);
  cuadricula(conFoto.map((h) => ({ img: imagenes.habilitacion[h._id], etiqueta: h.tipo })), 3, true);

  // ---- Observaciones y firmas: en la columna izquierda, abajo de la
  // habilitacion, si caben ahi; si no, abajo de todo a lo ancho.
  const ALTO_FIRMAS = 50;
  const enColumna = paginaTablas === paginaSeccion && finTablas + ALTO_FIRMAS < ALTO - M;
  if (enColumna) {
    doc.setPage(paginaTablas);
    y = finTablas - 2;
  } else if (paginaTablas > paginaImagenes) {
    doc.setPage(paginaTablas);
    y = finTablas;
  } else if (paginaImagenes > paginaTablas) {
    doc.setPage(paginaImagenes);
    y = yi;
  } else {
    doc.setPage(paginaTablas);
    y = Math.max(finTablas, yi);
  }
  if (!enColumna && y + ALTO_FIRMAS > ALTO - M) {
    doc.addPage();
    y = M + 4;
  }
  autoTable(doc, {
    startY: y + 2,
    body: [
      [{ content: "OBSERVACIONES:", styles: { fontStyle: "bold", textColor: VERDE } }, orden.observaciones || ""],
      [{ content: "MODELO DE REF.", styles: { fontStyle: "bold", textColor: VERDE } }, art.modelo || ""],
      [{ content: "ELABORADO POR", styles: { fontStyle: "bold", textColor: VERDE } }, orden.elaboradoPor || elaboradoPor],
      [{ content: "AUTORIZADO", styles: { fontStyle: "bold", textColor: VERDE } }, orden.autorizadoPor || ""],
      [{ content: "DISEÑO", styles: { fontStyle: "bold", textColor: VERDE } }, orden.diseno || ""],
      [{ content: "MAQUILERO", styles: { fontStyle: "bold", textColor: VERDE } }, orden.maquilero || ""],
    ],
    theme: "grid",
    styles: { fontSize: 8, cellPadding: 1.6, minCellHeight: 7 },
    columnStyles: { 0: { cellWidth: 34 } },
    margin: enColumna ? margenTablas : { left: M, right: M },
  });

  // Pie con numero de pagina
  const paginas = doc.getNumberOfPages();
  for (let p = 1; p <= paginas; p++) {
    doc.setPage(p);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(130);
    doc.text(`${folio} · Página ${p} de ${paginas}`, ANCHO / 2, ALTO - 4, {
      align: "center",
    });
  }
  doc.setTextColor(0);
  return doc;
}
